import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  CustomerAddress,
  CustomerAddressInput,
  CustomerInformationQuery__QueryKey,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  useReactQueryClient,
} from "../../../Common";

import { DataMutation_CreateCustomerAddress_QueryKey } from "./data-mutation--create-customer-address--query-key-factory";

export interface DataMutationCreateCustomerAddressConsumerInterface {
  data: DataMutationCreateCustomerAddress_Result["data"];
  status: DataMutationCreateCustomerAddress_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationCreateCustomerAddressConsumerInterface;
const DataMutationCreateCustomerAddressContext =
  React.createContext(defaultValue);

export interface DataMutationCreateCustomerAddress_ControllerInterface {
  mutate: DataMutationCreateCustomerAddress_Result["mutate"];
  mutateAsync: DataMutationCreateCustomerAddress_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationCreateCustomerAddress_ControllerInterface;
const DataMutationCreateCustomerAddress_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationCreateCustomerAddress_Request {
  address: CustomerAddressInput;
}
export type DataMutationCreateCustomerAddress_Result =
  ReactQuery.UseMutationResult<
    CustomerAddress,
    unknown,
    DataMutationCreateCustomerAddress_Request,
    unknown
  >;
export type GraphqlResponse_CreateCustomerAddress = GraphqlWrappedData<
  "createCustomerAddress",
  CustomerAddress
>;

export interface DataMutationCreateCustomerAddressProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationCreateCustomerAddressProvider: React.FunctionComponent<
  DataMutationCreateCustomerAddressProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const queryClient = useReactQueryClient();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_CreateCustomerAddress_QueryKey.createCustomerAddress();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CustomerAddress,
    DataMutationCreateCustomerAddress_Request
  > = React.useCallback(
    async (request) => {
      const street = request.address.street
        ? `["${request.address.street.join('","')}"]`
        : undefined;

      const gqlQuery = `mutation {
        createCustomerAddress(input: {
            ${
              request.address.region
                ? `region: {
                region: "${request.address.region.region || ""}"
                region_id: ${request.address.region.region_id}
                region_code: "${request.address.region.region_code || ""}"
            }`
                : ""
            }
            country_code: ${request.address.country_code}
            ${street ? `street: ${street}` : ""}
            ${
              request.address.telephone
                ? `telephone: "${request.address.telephone}"`
                : ""
            }
            ${
              request.address.postcode
                ? `postcode: "${request.address.postcode}"`
                : ""
            }
            ${request.address.city ? `city: "${request.address.city}"` : ""}
            ${
              request.address.firstname
                ? `firstname: "${request.address.firstname}"`
                : ""
            }
            ${
              request.address.lastname
                ? `lastname: "${request.address.lastname}"`
                : ""
            }
            default_shipping: ${false}
            default_billing: ${false}
        }) {
            id
            region {
              region
              region_code
            }
            country_code
            street
            telephone
            postcode
            city

            customer_id
            company
            fax
            firstname
            lastname
            middlename
            prefix
            suffix
            vat_id
            default_shipping
            default_billing
            extension_attributes {
              attribute_code
              value
            }
        }
      }`;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_CreateCustomerAddress>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      const { data: gqlResponseData } = data || {};
      const { createCustomerAddress } = gqlResponseData || {};

      return createCustomerAddress;
    },
    [initialState]
  );

  const handleSuccess = React.useCallback(() => {
    queryClient.invalidateQueries(
      CustomerInformationQuery__QueryKey.customerInformation()
    );
  }, [queryClient]);

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: handleSuccess,
  });

  const value: DataMutationCreateCustomerAddressConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
      }),
      [mutation.data, mutation.status]
    );
  const controllerValue: DataMutationCreateCustomerAddress_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationCreateCustomerAddressContext.Provider value={value}>
      <DataMutationCreateCustomerAddress_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationCreateCustomerAddress_ControllerContext.Provider>
    </DataMutationCreateCustomerAddressContext.Provider>
  );
};

export const useDataMutationCreateCustomerAddress = () => {
  const context = React.useContext(DataMutationCreateCustomerAddressContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationCreateCustomerAddress must be used within a DataMutationCreateCustomerAddressProvider"
    );
  }
  return context;
};

export const useDataMutationCreateCustomerAddress_asController = () => {
  const context = React.useContext(
    DataMutationCreateCustomerAddress_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationCreateCustomerAddress_asController must be used within a DataMutationCreateCustomerAddressProvider"
    );
  }
  return context;
};
